import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import { DataGrid, Spinner } from '@repo/components';
import { sortToString, stringToSort, constructQuery } from '@repo/shared/helpers';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { getUsersInReview, getGroupsInReview, getEventsInReview } from 'src/api/admin/sites';

interface ToReviewSearch {
	usersSort?: string;
	groupsSort?: string;
	eventsSort?: string;
	usersPage?: number;
	groupsPage?: number;
	eventsPage?: number;
	search?: string;
}

export const Route = createFileRoute('/_admin/$siteId/overview/to-review')({
	validateSearch: (search: Record<string, unknown>): ToReviewSearch => ({
		usersSort: search.usersSort as string | undefined,
		groupsSort: search.groupsSort as string | undefined,
		eventsSort: search.eventsSort as string | undefined,
		usersPage: search.usersPage as number | undefined,
		groupsPage: search.groupsPage as number | undefined,
		eventsPage: search.eventsPage as number | undefined,
		search: search.search as string | undefined,
	}),
	component: ToReviewPage,
});

const groupColumns = [
	{ field: 'name', headerName: 'Name', flex: 1, sortable: false },
	{
		field: 'reason',
		headerName: 'Reason',
		flex: 1,
		sortable: false,
		renderCell: () => 'Marked For Review',
	},
];

const eventColumns = [
	{ field: 'name', headerName: 'Name', flex: 1, sortable: false },
	{
		field: 'reason',
		headerName: 'Reason',
		flex: 1,
		sortable: false,
		renderCell: () => 'Marked For Review',
	},
];

const userColumns = [
	{ field: 'fullName', headerName: 'Name', flex: 1, sortable: false },
	{
		field: 'markedForReview',
		headerName: 'Marked For Review',
		flex: 1,
		sortable: false,
		renderCell: ({ row }: { row: { markedForReview?: string[] } }) =>
			row.markedForReview ? row.markedForReview.join(', ') : '',
	},
];

function ToReviewPage() {
	const { usersSort, groupsSort, eventsSort, usersPage, groupsPage, eventsPage, search } = Route.useSearch();
	const updateQueryParams = useUpdateQueryParams();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const permissions = authenticatedUser?.permissions || [];
	const verifyPermission = getVerifyPermission(permissions);

	const { data: usersResponse, isLoading: usersLoading } = useQuery({
		queryKey: ['users-in-review', usersSort, usersPage, search],
		queryFn: () => getUsersInReview(constructQuery({ sort: usersSort, page: usersPage, search })),
		enabled: verifyPermission(PERMISSIONS.adminUsersView),
	});

	const { data: groupsResponse, isLoading: groupsLoading } = useQuery({
		queryKey: ['groups-in-review', groupsSort, groupsPage, search],
		queryFn: () => getGroupsInReview(constructQuery({ sort: groupsSort, page: groupsPage, search })),
		enabled: verifyPermission(PERMISSIONS.adminGroupsView),
	});

	const { data: eventsResponse, isLoading: eventsLoading } = useQuery({
		queryKey: ['events-in-review', eventsSort, eventsPage, search],
		queryFn: () => getEventsInReview(constructQuery({ sort: eventsSort, page: eventsPage, search })),
		enabled: verifyPermission(PERMISSIONS.adminEventsView),
	});

	const isLoading = authLoading || usersLoading || groupsLoading || eventsLoading;

	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const users = usersResponse || { data: [], count: 0, page: 1, pageSize: 10 };
	const groups = groupsResponse || { data: [], count: 0, page: 1, pageSize: 10 };
	const events = eventsResponse || { data: [], count: 0, page: 1, pageSize: 10 };

	const handleGroupClick = ({ row }: { row: { id: string } }) => {
		window.open(`/groups/${row.id}`, '_blank');
	};

	const handleEventClick = ({ row }: { row: { id: string } }) => {
		window.open(`/events/${row.id}`, '_blank');
	};

	const handleUserClick = ({ row }: { row: { id: string } }) => {
		window.open(`/users/${row.id}`, '_blank');
	};

	const handlePaginationModelChange = (type: 'users' | 'groups' | 'events', { page }: { page: number }) => {
		updateQueryParams({ [`${type}Page`]: page });
	};

	const handleSort = (
		type: 'users' | 'groups' | 'events',
		[sortModel]: [{ field: string; sort: 'asc' | 'desc' } | undefined],
	) => {
		if (!sortModel) {
			return;
		}

		updateQueryParams({ [`${type}Sort`]: sortToString(sortModel) });
	};

	const nothingToReview = users.count === 0 && groups.count === 0 && events.count === 0;

	return (
		<div className="bg-white p-4">
			{nothingToReview && <div className="text-center text-gray-500">No items to review</div>}
			{users.count > 0 && (
				<>
					<h2 className="text-lg font-bold mb-4">Users</h2>
					<Paper variant="outlined" className="mb-4">
						<DataGrid
							data={users.data}
							columns={userColumns}
							rowCount={users.count}
							paginationMode="server"
							sortingMode="server"
							sortingOrder={['asc', 'desc']}
							sortModel={[stringToSort(usersSort || 'createdAt:desc')]}
							pageSize={users.pageSize}
							pageSizeOptions={[users.pageSize]}
							page={users.page}
							onPaginationModelChange={(data) => handlePaginationModelChange('users', data)}
							onSortModelChange={(data) => handleSort('users', data)}
							onReload={() => queryClient.invalidateQueries({ queryKey: ['users-in-review'] })}
							onRowClick={handleUserClick}
						/>
					</Paper>
				</>
			)}
			{groups.count > 0 && (
				<>
					<h2 className="text-lg font-bold mb-4">Companies</h2>
					<Paper variant="outlined" className="mb-4">
						<DataGrid
							data={groups.data}
							columns={groupColumns}
							rowCount={groups.count}
							paginationMode="server"
							sortingMode="server"
							sortingOrder={['asc', 'desc']}
							pageSize={groups.pageSize}
							pageSizeOptions={[groups.pageSize]}
							page={groups.page}
							sortModel={[stringToSort(groupsSort || 'createdAt:desc')]}
							onPaginationModelChange={(data) => handlePaginationModelChange('groups', data)}
							onSortModelChange={(data) => handleSort('groups', data)}
							onReload={() => queryClient.invalidateQueries({ queryKey: ['groups-in-review'] })}
							onRowClick={handleGroupClick}
						/>
					</Paper>
				</>
			)}
			{events.count > 0 && (
				<>
					<h2 className="text-lg font-bold mb-4">Events</h2>
					<Paper variant="outlined" className="mb-4">
						<DataGrid
							data={events.data}
							columns={eventColumns}
							rowCount={events.count}
							paginationMode="server"
							sortingMode="server"
							sortingOrder={['asc', 'desc']}
							sortModel={[stringToSort(eventsSort || 'createdAt:desc')]}
							pageSize={events.pageSize}
							pageSizeOptions={[events.pageSize]}
							page={events.page}
							onPaginationModelChange={(data) => handlePaginationModelChange('events', data)}
							onSortModelChange={(data) => handleSort('events', data)}
							onReload={() => queryClient.invalidateQueries({ queryKey: ['events-in-review'] })}
							onRowClick={handleEventClick}
						/>
					</Paper>
				</>
			)}
		</div>
	);
}
